<template>
  <div class="whistles-detail" style="margin-top: 6em; margin-bottom: 4em">
    <div class="container" style="max-width: 880px">
      <h1 class="text-center mb-3">whistles</h1>
      <p class="text-center text-muted mb-4">
        Whistles are where ironwood began, and I still build them on request —
        in wood, aluminium and brass, tuned in just intonation, in every key.
      </p>

      <img
        src="@/assets/img/products/whistle-low-d-lead.jpg"
        class="w-100 rounded mb-5"
        alt="A low D whistle in panacoco wood with aluminium sections"
      />

      <h2 class="mb-3">Features</h2>
      <ul class="feature-list mb-5">
        <li>
          Thin-walled wooden whistles — a wall of about 1.5&nbsp;mm, unless it
          is a very large whistle.
        </li>
        <li>
          Tunable, with a cork-sealed joint. On wooden whistles the mouthpiece
          is protected with an aluminium shell.
        </li>
        <li>Tuned in just or equal intonation.</li>
        <li>Available in every key.</li>
        <li>
          Oval tone holes on request (instead of round) — for better half-holing
          and easier covering on larger whistles.
        </li>
        <li>Offset G and/or E holes on request.</li>
      </ul>

      <h2 class="mb-3">Materials</h2>
      <p class="mb-3">
        <b>Wood.</b> Different hardwoods, from boxwood to grenadilla and more
        exotic sorts<CitesInfoComponent />
        There may be a surcharge for certain kinds of wood. The walls are very
        thin, and aluminium is used for the joint.
      </p>
      <p class="mb-3">
        <b>Aluminium.</b> Made from the aluminium alloy EN&nbsp;AW&nbsp;6060,
        which is suitable for kitchen utensils; the mouthpiece solder is
        lead-free. Tunable with a cork-sealed joint, with walls as thin as
        technically possible for a jointed whistle.
      </p>
      <p class="mb-5">
        <b>Brass.</b> A lead-free brass alloy with a hardwood fipple, tunable
        with a cork-sealed joint. Pure brass whistles can be made by arrangement
        but they'll be quite heavy.
      </p>

      <h2 class="mb-3">Prices</h2>
      <p>
        There is no online shop here — to order, just
        <LinkComponent href="/#contact" link-text="contact me" />.
      </p>
      <p class="mb-2">
        Prices are for a standard whistle, where I decide on tube diameter, wall
        thickness, hole size and position. I make whistles in
        <b>all keys</b> — the table lists a selection.
      </p>
      <table class="table table-striped align-middle">
        <thead>
          <tr class="table-dark">
            <th scope="col">Key</th>
            <th scope="col" class="text-end">Aluminium</th>
            <th scope="col" class="text-end">Brass / Wood</th>
            <th scope="col" class="text-end">Wood*</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in standardItems" :key="item.key" :class="item.class">
            <th scope="row" class="fw-normal">{{ item.key }}</th>
            <td class="text-end">{{ item.alu }}&nbsp;€</td>
            <td class="text-end">{{ item.brass }}&nbsp;€</td>
            <td class="text-end">{{ item.wood }}&nbsp;€</td>
          </tr>
        </tbody>
      </table>
      <p>* For certain types of wood they might be a surcharge.</p>
      <p class="text-muted mb-5">
        For a custom whistle with your own ideas and wishes, just
        <LinkComponent href="/#contact" link-text="contact me" />.
      </p>

      <h2 class="mb-3">Gallery</h2>
      <ImageCarouselComponent
        id="whistleGallery"
        :images="galleryImages"
        class="mb-5"
      />

      <p class="text-center">
        <LinkComponent
          href="/#contact"
          link-text="Contact me about a whistle"
        />
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import LinkComponent from "@/components/util/LinkComponent.vue";
import ImageCarouselComponent from "@/components/util/ImageCarouselComponent.vue";
import CitesInfoComponent from "@/components/general/CitesInfoComponent.vue";
import highd from "@/assets/img/products/gallery/whistle-highd.jpg";
import mezzo from "@/assets/img/products/gallery/whistle-mezzo.jpg";
import fippleAlu from "@/assets/img/products/gallery/whistle-fipple-alu.jpg";
import fippleBrass from "@/assets/img/products/gallery/whistle-fipple-brass.jpg";

export default defineComponent({
  name: "WhistlesView",
  components: { LinkComponent, ImageCarouselComponent, CitesInfoComponent },
  data() {
    return {
      galleryImages: [
        { src: highd, caption: "High D whistle in African padauk" },
        { src: mezzo, caption: "Mezzo A whistle in brass" },
        {
          src: fippleAlu,
          caption: "Fipple in ebony with aluminium shell of a panacoco whistle",
        },
        {
          src: fippleBrass,
          caption: "The mouthpiece of a brass whistle: layered wood and brass",
        },
      ],
      standardItems: [
        { key: "High G", alu: "195", brass: "205", wood: "365" },
        { key: "High F", alu: "195", brass: "205", wood: "365" },
        { key: "High E", alu: "195", brass: "205", wood: "365" },
        { key: "High Eb", alu: "195", brass: "205", wood: "365" },
        {
          key: "High D",
          alu: "195",
          brass: "205",
          wood: "365",
          class: "table-active",
        },
        { key: "High C", alu: "195", brass: "205", wood: "365" },
        { key: "High B", alu: "195", brass: "205", wood: "365" },
        { key: "High A", alu: "195", brass: "205", wood: "365" },
        { key: "Low G", alu: "245", brass: "265", wood: "490" },
        { key: "Low F", alu: "245", brass: "265", wood: "490" },
        { key: "Low E", alu: "245", brass: "265", wood: "490" },
        { key: "Low Eb", alu: "245", brass: "265", wood: "490" },
        { key: "Low D", alu: "245", brass: "265", wood: "490" },
        { key: "Low C", alu: "245", brass: "265", wood: "490" },
        { key: "Low B", alu: "295", brass: "345", wood: "695" },
        { key: "Low A", alu: "295", brass: "345", wood: "695" },
      ],
    };
  },
});
</script>

<style scoped>
.feature-list li {
  margin-bottom: 0.6rem;
}
</style>

<style src="@/assets/css/style.css"></style>
